import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { groupsAPI } from '../api';
import { useAuth } from '../AuthContext';

function JoinGroup() {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [groupPreview, setGroupPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);

  const handleJoinGroup = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await groupsAPI.joinGroup(inviteCode);
      setSuccess(true);
      setTimeout(() => {
        navigate(`/groups/${response.data.group.id}`);
      }, 1500);
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to join group';
      const groupId = err.response?.data?.group_id;

      // If user is already a member, redirect to the group page
      if (errorMessage === 'Already a member of this group' && groupId) {
        navigate(`/groups/${groupId}`);
        return;
      }

      setError(errorMessage);
      setLoading(false);
    }
  };

  // Load group preview
  useEffect(() => {
    const loadGroupPreview = async () => {
      try {
        const response = await groupsAPI.getGroupPreview(inviteCode);
        setGroupPreview(response.data.group);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load group information');
      } finally {
        setPreviewLoading(false);
      }
    };

    loadGroupPreview();
  }, [inviteCode]);

  // Handle authentication and redirect
  useEffect(() => {
    // Don't do anything while auth is still loading
    if (authLoading) return;

    if (!isAuthenticated) {
      // Store invite code and redirect to login
      sessionStorage.setItem('pendingInviteCode', inviteCode);
      navigate('/login');
    } else {
      // Clear any pending invite code if exists
      const pendingCode = sessionStorage.getItem('pendingInviteCode');
      if (pendingCode) {
        sessionStorage.removeItem('pendingInviteCode');
      }
    }
  }, [isAuthenticated, authLoading, inviteCode, navigate]);

  if (!isAuthenticated) {
    return null;
  }

  if (previewLoading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="loading">
            <div className="spinner"></div>
            <p style={{ marginTop: '1rem', textAlign: 'center' }}>Loading group information...</p>
          </div>
        </div>
      </div>
    );
  }

  // Get theme styles
  const getThemeStyles = (theme) => {
    const themes = {
      default: {
        headerColor: '#2c5f8d',
        accentColor: '#3a7bc8',
        icon: '✨'
      },
      festive: {
        headerColor: '#c41e3a',
        accentColor: '#146b3a',
        icon: '🎄'
      },
      elegant: {
        headerColor: '#2d2d2d',
        accentColor: '#7e5a9b',
        icon: '💎'
      },
      fun: {
        headerColor: '#ff6b6b',
        accentColor: '#4ecdc4',
        icon: '🎉'
      }
    };
    return themes[theme] || themes.default;
  };

  const template = groupPreview?.template || {};
  const themeStyles = getThemeStyles(template.theme);

  return (
    <div className="auth-container">
      <div className="auth-card" style={{ background: template.background_color || '#f8f9fa' }}>
        <h2>Join Secret Santa Group</h2>

        {error && !groupPreview && <div className="error-message">{error}</div>}

        {success && (
          <div className="success-message">
            Successfully joined the group! Redirecting...
          </div>
        )}

        {groupPreview && !success && (
          <div>
            {template.welcome_message && (
              <div style={{
                padding: '1rem',
                marginBottom: '1.5rem',
                background: 'rgba(255, 255, 255, 0.7)',
                borderRadius: '10px',
                borderLeft: `4px solid ${themeStyles.accentColor}`,
                fontStyle: 'italic',
                color: '#333'
              }}>
                {template.welcome_message}
              </div>
            )}

            <div style={{
              background: 'rgba(255, 255, 255, 0.9)',
              padding: '1.5rem',
              borderRadius: '10px',
              marginBottom: '1.5rem',
              border: `2px solid ${themeStyles.accentColor}`
            }}>
              <h3 style={{
                marginTop: 0,
                marginBottom: '0.75rem',
                color: themeStyles.headerColor,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <span style={{ fontSize: '1.5rem' }}>{themeStyles.icon}</span>
                {groupPreview.name}
              </h3>
              {groupPreview.description && (
                <p style={{ color: '#666', marginBottom: '1rem' }}>{groupPreview.description}</p>
              )}
              <div style={{ fontSize: '0.9rem', color: '#555' }}>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Organizer:</strong> {groupPreview.organizer_name}
                </p>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Members:</strong> {groupPreview.member_count}
                </p>
                <p style={{ marginBottom: 0 }}>
                  <strong>Status:</strong> {groupPreview.is_assigned ? 'Assignments Created' : 'Accepting Members'}
                </p>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {groupPreview.is_assigned ? (
              <div>
                <p style={{ color: '#856404', textAlign: 'center', marginBottom: '1rem' }}>
                  This group has already created Secret Santa assignments. You cannot join at this time.
                </p>
                <button
                  className="btn btn-secondary"
                  style={{ width: '100%' }}
                  onClick={() => navigate('/groups')}
                >
                  Back to My Groups
                </button>
              </div>
            ) : (
              <div>
                {!loading && (
                  <button
                    className="btn btn-primary"
                    style={{
                      width: '100%',
                      background: themeStyles.accentColor,
                      borderColor: themeStyles.accentColor
                    }}
                    onClick={handleJoinGroup}
                  >
                    {template.button_text || 'Join Group'}
                  </button>
                )}
                {loading && (
                  <div className="loading">
                    <div className="spinner"></div>
                    <p style={{ marginTop: '1rem', textAlign: 'center' }}>Joining group...</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!groupPreview && error && (
          <button
            className="btn btn-secondary"
            style={{ width: '100%', marginTop: '1rem' }}
            onClick={() => navigate('/groups')}
          >
            Back to My Groups
          </button>
        )}
      </div>
    </div>
  );
}

export default JoinGroup;
